import { Grid, Button, Typography, Select, MenuItem, TextField } from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Delete, Check } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import api from '../../../../services/api';
import { Title } from '../../../../components/atoms/title';
import { colors } from '../../../../styles/colors';
import ResponsiveDialog from '../../../../components/molecules/responsiveDialog';
import { useUniversities } from '../../../../hooks/universities';
import { InfoInput, InfoItem } from './styles';

export function UserStatus({ userData, infoToChange }) {
  const queryClient = useQueryClient();
  const { genders, majors, universities } = useUniversities();

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);

  const formatDate = dateString => {
    const date = new Date(dateString);
    return format(new Date(date), 'dd/MM/yyyy HH:mm');
  };

  const getMajor = majorId => {
    return majors.find(major => major?.id === majorId);
  };

  const getUniversity = universityId => {
    return universities.find(university => university?.id === universityId);
  };

  const [loading, setLoading] = useState(true);
  const [selectedUserGender, setSelectedUserGender] = useState(userData?.user.gender);
  const [selectedUniversity, setSelectedUniversity] = useState(getUniversity(userData?.user.university_id));
  const [selectedMajor, setSelectedMajor] = useState(getMajor(userData?.user.major_id));
  const [isApproved, setIsApproved] = useState(userData?.user.approved);

  useEffect(() => {
    setSelectedUserGender(userData?.user.gender);
    setSelectedUniversity(getUniversity(userData?.user.university_id));
    setSelectedMajor(getMajor(userData?.user.major_id));
    setIsApproved(userData?.user.approved);
    if (universities.length > 0 && majors.length > 0) {
      setLoading(false);
    }
  }, [userData, universities, majors]);

  const imageUrls = userData?.images.map(image => image.url);

  const handleSubmitForm = async e => {
    e.preventDefault();
    await updateUserMutation.mutateAsync({
      first_name: e.target.first_name.value,
      last_name: e.target.last_name.value,
      email: e.target.email.value,
      birthday: e.target.birthday.value,
      gender: selectedUserGender,
      university_id: selectedUniversity?.id,
      major_id: selectedMajor?.id,
    });
  };

  const handleUnban = useMutation(
    async reason => {
      await api.post(`/admin/users/unban`, {
        email: userData?.user.email,
        reason,
      });
    },
    {
      onSuccess: () => {
        toast('Usuário desbanido com sucesso!', { type: 'success' });
      },
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['issue-user', userData?.user.email] });
        const previousUserData = queryClient.getQueryData(['issue-user', userData?.user.email]);
        queryClient.setQueryData(['issue-user', userData?.user.email], old => ({
          ...old,
          user: {
            ...old.user,
            banned: false,
          },
        }));
        return { previousUserData };
      },
      onSettled: () => {
        queryClient.invalidateQueries(['issue-user', userData?.user.email]);
      },
      onError: context => {
        toast('Erro ao desbanir usuário!', { type: 'error' });
        queryClient.setQueryData(['issue-user', userData?.user.email], context.previousUserData);
      },
    }
  );

  const updateUserMutation = useMutation(
    async user => {
      await api.put(`/admin/users/${userData?.user.id}`, { ...user });
    },
    {
      onSuccess: () => toast('Usuário atualizado com sucesso!', { type: 'success' }),
      onError: () => {
        toast('Erro ao atualizar usuário!', { type: 'error' });
      },
    }
  );

  const deleteUserMutation = useMutation(
    async () => {
      await api.delete(`/admin/users/${userData?.user.email}`);
    },
    {
      onSuccess: () => toast('Usuário deletado com sucesso!', { type: 'success' }),
      onError: () => toast('Erro ao deletar usuário!', { type: 'error' }),
      onSettled: () => {
        setDeleteConfirmationDialogOpen(false);
      },
    }
  );

  const getSubscriptionName = subscriptionType => {
    return subscriptionType === 'premium'
      ? 'Plus'
      : subscriptionType === 'premium10'
      ? 'Premium'
      : subscriptionType === 'basic'
      ? 'Basic'
      : 'Nenhum';
  };

  const confirmEmailMutation = useMutation(
    async () => {
      await api.put(`/activate`, { token: userData?.user.token });
    },
    {
      onSuccess: () => {
        setIsApproved(true);
        toast('Email confirmado com sucesso!', { type: 'success' });
      },
      onError: () => {
        toast('Erro ao confirmar email!', { type: 'error' });
      },
    }
  );

  return (
    userData && (
      <>
        <Grid container>
          <Grid item>
            <Title>Status de usuário</Title>
          </Grid>

          <Grid item xs={12}>
            {deleteConfirmationDialogOpen && (
              <ResponsiveDialog
                handleConfirm={deleteUserMutation.mutate}
                handleClose={() => setDeleteConfirmationDialogOpen(false)}
                title='Atenção'
                content='Tem certeza que deseja deletar esse usuário? Essa ação não tem volta'
                confirmationString='Deletar usuário'
                loading={deleteUserMutation.isLoading}
                open={deleteConfirmationDialogOpen}
              />
            )}
          </Grid>
        </Grid>
        <form onSubmit={handleSubmitForm} key={userData.user.id}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div style={{ padding: '16px', marginBottom: '32px', border: '1px solid #ccc', borderRadius: '4px' }}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Grid
                      style={{ padding: '8px 16px 8px 16px' }}
                      container
                      direction='column'
                      alignItems='flex-start'
                      spacing={1}
                    >
                      <Typography variant='body1'>
                        {userData?.user.first_name} {userData?.user.last_name}{' '}
                        {isApproved ? (
                          '(Aprovado)'
                        ) : (
                          <>
                            (Não aprovado){' '}
                            <Button style={{ color: colors.primary_blue }} onClick={confirmEmailMutation.mutate}>
                              <Check /> Confirmar e-mail
                            </Button>
                          </>
                        )}
                      </Typography>

                      <InfoItem>
                        <strong>ID: </strong>
                        {userData?.user.id}
                      </InfoItem>

                      <InfoItem>
                        <strong>Email: </strong>{' '}
                        <InfoInput name='email' type='email' defaultValue={userData?.user?.email} required />
                      </InfoItem>

                      <InfoItem>
                        <strong style={{ color: infoToChange?.first_name ? 'red' : 'black' }}>Primeiro Nome: </strong>
                        <InfoInput name='first_name' defaultValue={userData?.user.first_name} required />
                      </InfoItem>

                      <InfoItem>
                        <strong>Sobrenome: </strong>
                        <InfoInput name='last_name' defaultValue={userData?.user.last_name} required />
                      </InfoItem>
                      <InfoItem>
                        <strong style={{ color: infoToChange?.handle ? 'red' : 'black' }}>Handle: </strong>
                        <InfoInput name='handle' defaultValue={userData?.user.handle} />
                      </InfoItem>

                      <InfoItem>
                        <strong>Bio:</strong> {userData?.user.bio}
                      </InfoItem>

                      <InfoItem>
                        <strong style={{ color: infoToChange?.birthday ? 'red' : 'black' }}>
                          Data de nascimento:{' '}
                        </strong>
                        {
                          /* differenceInYears(new Date(), new Date(userData?.user.birthday)) */
                          <InfoInput name='birthday' type='date' defaultValue={userData?.user.birthday} required />
                        }
                      </InfoItem>

                      <InfoItem>
                        <strong style={{ color: infoToChange?.gender ? 'red' : 'black' }}>Gênero: </strong>
                        <Select
                          value={selectedUserGender}
                          style={{ height: '30px' }}
                          onChange={e => setSelectedUserGender(e.target.value)}
                        >
                          {genders.map(gender => {
                            return (
                              <MenuItem value={gender.value} key={gender.value}>
                                {gender.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </InfoItem>

                      <InfoItem>
                        <strong>Preferência: </strong>
                        {userData?.user?.preference}
                      </InfoItem>

                      <InfoItem>
                        <strong>Score: </strong>
                        {userData?.user?.score}
                      </InfoItem>

                      <InfoItem>
                        <strong>Appeal: </strong>
                        {userData?.user?.appeal}
                      </InfoItem>

                      <InfoItem>
                        <strong>Seletividade: </strong>
                        {userData?.user?.selectivity}
                      </InfoItem>

                      <InfoItem>
                        <strong>Likes dados: </strong>
                        {userData?.user?.likes_given}
                      </InfoItem>

                      <InfoItem>
                        <strong>Likes recebidos: </strong>
                        {userData?.user?.likes_received}
                      </InfoItem>

                      <InfoItem>
                        <strong>Plano de assinatura: </strong> {getSubscriptionName(userData?.user?.subscription_type)}
                      </InfoItem>

                      <InfoItem>
                        <strong>Criado em: </strong>
                        {formatDate(userData?.user.created_at)}
                      </InfoItem>

                      <InfoItem>
                        <strong>Último acesso: </strong>
                        {formatDate(userData?.user.last_active)}
                      </InfoItem>

                      <InfoItem>
                        <strong>E-mail confirmado: </strong>
                        {userData?.user.active ? 'sim' : 'não'}
                      </InfoItem>

                      <InfoItem style={{ marginBottom: userData?.user.banned ? '6px' : 0 }}>
                        <strong>Conta banida: </strong>
                        {userData?.user.banned ? 'sim' : 'não'}
                      </InfoItem>

                      {userData?.user.banned && (
                        <div style={{ marginBottom: 0 }}>
                          <strong> Desbanir: </strong>
                          <Button onClick={() => handleUnban.mutate('unfair')}>Ban injusto</Button>
                          <Button onClick={() => handleUnban.mutate('second chance')}>Segunda chance</Button>
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <strong style={{ color: infoToChange?.major_id ? 'red' : 'black', marginRight: '1em' }}>
                          Curso:
                        </strong>
                        <div style={{ flex: 1 }}>
                          {loading ? (
                            <div>Loading...</div>
                          ) : (
                            <Autocomplete
                              id='major'
                              value={selectedMajor}
                              options={majors}
                              getOptionLabel={option => option.name ?? 'CURSO PROIBIDO, NÃO SELECIONE'}
                              getOptionKey={option => option.id}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              onChange={(e, value) => {
                                setSelectedMajor(value);
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  variant='outlined'
                                  margin='normal'
                                  fullWidth
                                  InputProps={{
                                    ...params.InputProps,
                                    style: {
                                      padding: '0 8px',
                                    },
                                  }}
                                />
                              )}
                            />
                          )}
                        </div>
                      </div>

                      <InfoItem style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <strong style={{ color: infoToChange?.university_id ? 'red' : 'black', marginRight: '1em' }}>
                          Universidade:
                        </strong>
                        <div style={{ flex: 1 }}>
                          {loading ? (
                            <div>Loading...</div>
                          ) : (
                            <Autocomplete
                              id='university'
                              value={selectedUniversity}
                              options={universities}
                              getOptionKey={option => option.id}
                              getOptionLabel={option => option.name ?? 'FACULDADE PROIBIDA, NÃO SELECIONE'}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              onChange={(e, value) => {
                                setSelectedUniversity(value);
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  variant='outlined'
                                  margin='normal'
                                  fullWidth
                                  InputProps={{
                                    ...params.InputProps,
                                    style: {
                                      padding: '0px 8px',
                                    },
                                  }}
                                />
                              )}
                            />
                          )}
                        </div>
                      </InfoItem>

                      <InfoItem>
                        <strong>Plataforma: </strong>
                        {userData?.user.platform}
                      </InfoItem>

                      {userData?.user && (
                        <div>
                          <Button
                            type='submit'
                            style={{ marginTop: '12px', paddingLeft: '0', color: colors.primary_blue }}
                          >
                            <Check /> Salvar Edição
                          </Button>
                          <Button
                            style={{ marginTop: '12px', color: colors.alert_red }}
                            onClick={() => {
                              setDeleteConfirmationDialogOpen(true);
                            }}
                          >
                            <>
                              <Delete /> Deletar usuário
                            </>
                          </Button>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                  {imageUrls && (
                    <Grid item xs={6}>
                      <Grid container direction='row' spacing={1}>
                        {imageUrls?.map((url, index) => {
                          let gridSize = 6;
                          if (imageUrls.length > 2) {
                            gridSize = 4;
                          }
                          return (
                            <Grid item xs={gridSize} key={url}>
                              <div
                                style={{
                                  width: '100%',
                                  maxWidth: '350px',
                                  height: '0',
                                  paddingBottom: '142.85%',
                                  position: 'relative',
                                  overflow: 'hidden',
                                }}
                                key={url}
                              >
                                <img
                                  src={url}
                                  alt={`User ${index}`}
                                  style={{
                                    position: 'absolute',
                                    top: '0',
                                    left: '0',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                  }}
                                />
                              </div>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </div>
            </Grid>
          </Grid>
        </form>
      </>
    )
  );
}
